import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Cupom,
  CupomInput,
  criarCupom,
  atualizarCupom,
} from '../../services/cupomService';
import { LISTAR_TORNEIOS, Torneio, ListarTorneiosData } from '../../services/torneioService';
import { cupomSchema, CupomFormValues } from '../../schemas/cupomSchemas';

export interface CupomModalProps {
  open: boolean;
  onClose: () => void;
  cupom?: Cupom;
  onSaved?: (cupom: Cupom) => void;
}

const CupomModal: React.FC<CupomModalProps> = ({
  open,
  onClose,
  cupom,
  onSaved,
}) => {
  type CupomFormValuesEx = CupomFormValues & { torneioId?: string };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<CupomFormValuesEx>({ resolver: zodResolver(cupomSchema.extend({ torneioId: z.string().optional() })) });

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [vincularTorneio, setVincularTorneio] = useState<boolean>(false);

  const { data: torneiosData, loading: loadingTorneios } = useQuery<ListarTorneiosData>(LISTAR_TORNEIOS, {
    fetchPolicy: 'cache-first',
  });

  const torneios: Pick<Torneio, 'id' | 'nome'>[] = useMemo(
    () => (torneiosData?.torneios || []).map(t => ({ id: t.id, nome: t.nome })),
    [torneiosData],
  );

  useEffect(() => {
    if (open) {
      if (cupom) {
        reset({
          codigo: cupom.codigo,
          tipo: cupom.tipo,
          valor: cupom.valor,
          quantidadeDisponivel: cupom.quantidadeDisponivel,
          status: cupom.status,
          torneioId: cupom.torneio?.id || '',
          dataExpiracao: cupom.dataExpiracao ? cupom.dataExpiracao.slice(0, 16) : '',
        });
        setVincularTorneio(!!cupom.torneio?.id);
      } else {
        reset({
          codigo: '',
          tipo: 'FIXO',
          valor: 0,
          quantidadeDisponivel: 0,
          status: 'ATIVO',
          torneioId: '',
          dataExpiracao: '',
        });
        setVincularTorneio(false);
      }
      setTimeout(() => setFocus('codigo'), 0);
    }
  }, [open, cupom, reset, setFocus]);

  if (!open) return null;

  const onSubmit = async (values: CupomFormValuesEx) => {
    setErrorMsg(null);
    const input: CupomInput = {
      codigo: values.codigo,
      tipo: values.tipo,
      valor: values.valor,
      quantidadeDisponivel: values.quantidadeDisponivel,
      status: values.status,
      torneioId: vincularTorneio ? values.torneioId || null : null,
      dataExpiracao: values.dataExpiracao || null,
    };
    try {
      const saved = cupom
        ? await atualizarCupom(cupom.codigo, input)
        : await criarCupom(input);
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setErrorMsg('Erro ao salvar cupom');
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show d-block" role="dialog" tabIndex={-1}>
        <div className="modal-dialog" role="document">
          <form className="modal-content" onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-header">
              <h5 className="modal-title w-100 text-center">
                {cupom ? 'Editar cupom' : 'Novo cupom'}
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={onClose}
                disabled={isSubmitting}
              ></button>
            </div>
            <div className="modal-body">
              {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}
              <div className="d-flex justify-content-between flex-wrap gap-3 mb-3">
                <div className="mb-3 col-5">
                  <label className="form-label">Código</label>
                  <input
                    className="form-control"
                    aria-label="Código"
                    {...register('codigo')}
                  />
                  {errors.codigo && (
                    <span className="text-danger small">{errors.codigo.message}</span>
                  )}
                </div>
                <div className="mb-3 col-3">
                  <label className="form-label">Tipo</label>
                  <select className="form-select form-select-lg" aria-label="Tipo" {...register('tipo')}>
                    <option value="FIXO">R$</option>
                    <option value="PORCENTAGEM">%</option>
                  </select>
                  {errors.tipo && (
                    <span className="text-danger small">{errors.tipo.message}</span>
                  )}
                </div>
                <div className="mb-3 col-3">
                  <label className="form-label">Valor</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    aria-label="Valor"
                    {...register('valor', { valueAsNumber: true })}
                  />
                  {errors.valor && (
                    <span className="text-danger small">{errors.valor.message}</span>
                  )}
                </div>
                <div className="mb-3 col-7">
                  <label className="form-label">Quantidade disponível</label>
                  <input
                    type="number"
                    className="form-control"
                    aria-label="Quantidade disponível"
                    {...register('quantidadeDisponivel', { valueAsNumber: true })}
                  />
                  {errors.quantidadeDisponivel && (
                    <span className="text-danger small">{errors.quantidadeDisponivel.message}</span>
                  )}
                </div>
                <div className="mb-3 col-3">
                  <label className="form-label">Status</label>
                  <select className="form-select form-select-lg" aria-label="Status" {...register('status')}>
                    <option value="ATIVO">Ativo</option>
                    <option value="INATIVO">Inativo</option>
                  </select>
                  {errors.status && (
                    <span className="text-danger small">{errors.status.message}</span>
                  )}
                </div>
                <div className="mb-3 col-12">
                  <label className="form-label" htmlFor="dataExpiracao">Data de expiração</label>
                  <input
                    type="datetime-local"
                    id="dataExpiracao"
                    className="form-control"
                    aria-label="Data de expiração"
                    {...register('dataExpiracao')}
                  />
                </div>
                <div className="mb-3 col-12">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="vincularTorneio"
                      checked={vincularTorneio}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setVincularTorneio(checked);
                        if (!checked) setValue('torneioId', '');
                      }}
                    />
                    <label className="form-check-label" htmlFor="vincularTorneio">
                      Vincular cupom a um torneio.
                    </label>
                  </div>
                </div>
                <div className="mb-3 col-12">
                  <label className="form-label" htmlFor="torneio">Torneio</label>
                  <select
                    id="torneio"
                    className='form-select form-select-lg'
                    aria-label="Torneio"
                    disabled={!vincularTorneio || loadingTorneios}
                    {...register('torneioId')}
                  >
                    <option value="">{loadingTorneios ? 'Carregando...' : 'Selecione o torneio'}</option>
                    {torneios.map((t) => (
                      <option key={t.id} value={t.id}>{t.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-link"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CupomModal;

